import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid } from 'recharts';

import { api } from '@/lib/api';
import { fmtInt, fmtUsd } from '@/lib/format';

interface Summary {
  wines_total: number;
  bottles: number;
  invested_usd: number;
  sold_usd: number;
  gifts_received_usd: number;
  estimated_value_usd: number;
  by_winery: { winery: string; bottles: number; wines: number }[];
  by_type: { type: string; cnt: number }[];
}

interface ValuePoint {
  month: string;
  invested_usd: number;
  bottles: number;
}

function Kpi({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="kpi">
      <p className="kpi-label">{label}</p>
      <p className="kpi-value">{value}</p>
      {hint && <p className="kpi-hint">{hint}</p>}
    </div>
  );
}

export function DashboardPage() {
  const summary = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: async () => (await api.get<{ data: Summary }>('/dashboard/summary')).data.data,
  });

  const history = useQuery({
    queryKey: ['dashboard', 'history'],
    queryFn: async () => (await api.get<{ data: ValuePoint[] }>('/dashboard/value-history?months=12')).data.data,
  });

  const s = summary.data;
  const delta = (s?.estimated_value_usd ?? 0) - (s?.invested_usd ?? 0);
  const deltaHint = delta !== 0
    ? `${delta > 0 ? '+' : ''}${fmtUsd(delta)} vs costo`
    : undefined;

  return (
    <div className="px-10 py-7 max-w-5xl">
      <p className="text-[12px] text-notion-500 mb-1">Mi cava › Dashboard</p>
      <h1>Dashboard</h1>
      <p className="text-[13px] text-notion-600 mt-1 mb-7">
        Resumen de tu cava: stock, inversión, valor estimado y obsequios recibidos.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-9">
        <Kpi label="Vinos" value={fmtInt(s?.wines_total)} />
        <Kpi label="Botellas" value={fmtInt(s?.bottles)} />
        <Kpi label="Invertido" value={fmtUsd(s?.invested_usd)} />
        <Kpi
          label="Valor estimado"
          value={fmtUsd(s?.estimated_value_usd)}
          hint={deltaHint}
        />
        <Kpi label="Regalos" value={fmtUsd(s?.gifts_received_usd)} />
        <Kpi label="Vendido" value={fmtUsd(s?.sold_usd)} />
      </div>

      <section className="mb-9">
        <h2 className="kpi-label mb-3">Inversión acumulada (USD)</h2>
        <div className="h-64">
          {history.data && history.data.length > 0 ? (
            <ResponsiveContainer>
              <LineChart data={history.data} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1ef" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#787774' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#787774' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v: number) => fmtUsd(v)}
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e9e9e7',
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Line type="monotone" dataKey="invested_usd" stroke="#37352f" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[13px] text-notion-500 italic">
              Cuando registres compras vas a ver acá la evolución de tu inversión.
            </p>
          )}
        </div>
      </section>

      <section className="mb-9">
        <h2 className="kpi-label mb-3">Top bodegas</h2>
        {s?.by_winery && s.by_winery.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={s.by_winery} layout="vertical" margin={{ left: 10, top: 4, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1ef" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#787774' }} axisLine={false} tickLine={false} />
                <YAxis dataKey="winery" type="category" width={140} tick={{ fontSize: 11, fill: '#787774' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e9e9e7',
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="bottles" fill="#37352f" radius={2} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-[13px] text-notion-500 italic">Sin datos todavía.</p>
        )}
      </section>
    </div>
  );
}
